import { Link, router } from '@inertiajs/react'

import AdminLayout from '../../../components/AdminLayout'
import { AdminCard, AdminHead } from '../../../components/admin/AdminHead'
import { ImagePlaceholderIcon } from '../../../components/Icons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface Product {
  id: number
  name: string
  price: number
  stock: number
  productType: string
  customizable: boolean
  listed: boolean
  stepCount: number
  imageUrl: string | null
  createdAt: string
}

interface Props {
  products: Product[]
}

export default function AdminProductsIndex({ products }: Props) {
  const handleDelete = (product: Product) => {
    if (confirm(`Delete "${product.name}"? This can't be undone.`)) {
      router.delete(`/admin/products/${product.id}`)
    }
  }

  return (
    <AdminLayout>
      <AdminHead
        eyebrow="Catalog"
        title="Products"
        sub={`${products.length} total`}
        actions={
          <Button asChild variant="primary" size="compact">
            <Link href="/admin/products/new">Add product</Link>
          </Button>
        }
      />

      <AdminCard>
        {products.length === 0 ? (
          <div className="p-[18px]">
            <EmptyState
              title="No products yet."
              description="Create your first piece to open the drop."
              action={
                <Button asChild variant="outline" size="compact">
                  <Link href="/admin/products/new">Add product</Link>
                </Button>
              }
            />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-surface-sunk">
                <TableHead>Product</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead>Visibility</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => (
                <TableRow key={product.id} flagged={product.stock === 0 || !product.listed}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className="size-9 shrink-0 overflow-hidden rounded-sm bg-surface-sunk">
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            className="size-full object-cover"
                          />
                        ) : (
                          <span className="flex size-full items-center justify-center text-ink-faint">
                            <ImagePlaceholderIcon size={16} />
                          </span>
                        )}
                      </span>
                      <span>
                        {product.name}
                        {product.customizable && (
                          <span className="block text-[11px] text-ink-muted">
                            Configurator · {product.stepCount} step(s)
                          </span>
                        )}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {product.productType === 'one_of_one' ? '1 of 1' : product.productType === 'custom' ? 'Custom' : 'Batch'}
                    </Badge>
                  </TableCell>
                  <TableCell className="tabular-nums">${product.price.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant={product.stock === 0 ? 'accent' : 'outline'}>
                      {product.stock === 0 ? 'Sold out' : `${product.stock} in stock`}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={product.listed ? 'ink' : 'tint'}>
                      {product.listed ? 'Listed' : 'Unlisted'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-4">
                      {product.customizable && (
                        <Link
                          href={`/admin/products/${product.id}/customization`}
                          className="text-[13px] font-medium underline underline-offset-4 hover:text-signal-deep"
                        >
                          Steps
                        </Link>
                      )}
                      <Link
                        href={`/admin/products/${product.id}/edit`}
                        className="text-[13px] font-medium underline underline-offset-4 hover:text-signal-deep"
                      >
                        Edit
                      </Link>
                      <Button
                        variant="inline"
                        size="inline"
                        className="text-[10px] text-ink-muted"
                        onClick={() => handleDelete(product)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminCard>
    </AdminLayout>
  )
}
